import { makeAutoObservable, observableRef } from 'mobx'

/** save: save the form, then go · leave: drop the edits and go · stay: keep editing. */
export type LeaveChoice = 'save' | 'leave' | 'stay'

/** One form with unsaved edits. The hook that registers it owns and updates it. */
export type LeaveGuard = {
  /** Restores the form to its saved values. */
  onRevert?: () => void
  /** Saves the form; resolves whether it saved. Without it the prompt offers no «Да». */
  save?: () => Promise<boolean>
  /** Also block query-string changes (a record panel keyed by `?sheet=`). */
  watchSearch: boolean
  /** Stops guarding until the form is clean again; called once the user chose to leave. */
  release: () => void
}

type Place = { pathname: string; search: string }

/**
 * Registry of dirty forms and the one "unsaved changes" prompt they share.
 * The router blocker in the shell and in-form close buttons both ask here, so
 * every form answers the same dialog: «Данные были изменены. Сохранить
 * изменения?» Да (save, then go) · Нет (go without saving) · Отмена (stay).
 */
export class LeaveGuardStore {
  private guards = new Set<LeaveGuard>()
  /** The open prompt; LeaveGuardHost renders it and resolves it. */
  pending: { canSave: boolean; resolve: (choice: LeaveChoice) => void } | null = null

  constructor() {
    // `pending` by reference: a deep observable would wrap `resolve`, breaking the identity check in ask().
    makeAutoObservable<this, 'guards'>(this, { guards: false, pending: observableRef })
  }

  register(guard: LeaveGuard) {
    this.guards.add(guard)
    return () => {
      this.guards.delete(guard)
    }
  }

  get hasGuards() {
    return this.guards.size > 0
  }

  shouldBlock(current: Place, next: Place) {
    if (this.guards.size === 0) return false
    if (current.pathname !== next.pathname) return true
    return current.search !== next.search && [...this.guards].some((guard) => guard.watchSearch)
  }

  /** Every dirty form can save itself, so the prompt may offer «Да». */
  get canSaveAll() {
    return this.guards.size > 0 && [...this.guards].every((guard) => guard.save)
  }

  ask(canSave: boolean): Promise<LeaveChoice> {
    // A newer request supersedes an unanswered one; the older asker stays put.
    this.pending?.resolve('stay')
    return new Promise((resolve) => {
      const settle = (choice: LeaveChoice) => {
        if (this.pending?.resolve === settle) this.pending = null
        resolve(choice)
      }
      this.pending = { canSave, resolve: settle }
    })
  }

  /** Saves every dirty form, one after another; false once one of them did not save (it stays open with its errors). */
  async saveAll() {
    for (const guard of [...this.guards]) {
      if (!guard.save || !(await guard.save())) return false
    }
    return true
  }

  /** Leaving a `?sheet=` panel also drops the edits of the tabs inside it, so they must not ask again. */
  releaseQueryBound() {
    for (const guard of [...this.guards]) if (guard.watchSearch) guard.release()
  }

  releaseAll() {
    for (const guard of [...this.guards]) guard.release()
  }
}
